/**
 * Import target for PokerStars HH `.txt` files. Accepts drag-drop and a
 * file input. Pipes through `importTournamentTxt` (M4) and calls back
 * with a status message for the toast. Multiple files are imported
 * sequentially so a single malformed one doesn't abort the rest.
 */
import { useCallback, useRef, useState } from 'react';
import { importTournamentTxt } from '@/storage/handHistory';
import { HhParseError } from '@/storage/pokerstars';

export type ImportResult =
  | { kind: 'ok'; tournamentId: string; handCount: number }
  | { kind: 'error'; filename: string; message: string };

export type HhFileDropzoneProps = {
  onResults: (results: ReadonlyArray<ImportResult>) => void;
};

export function HhFileDropzone({ onResults }: HhFileDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const [importing, setImporting] = useState(false);

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      setImporting(true);
      const results: ImportResult[] = [];
      for (const file of Array.from(files)) {
        try {
          const text = await file.text();
          const { tournamentId, handIds } = await importTournamentTxt(text);
          results.push({ kind: 'ok', tournamentId, handCount: handIds.length });
        } catch (e) {
          const msg = e instanceof HhParseError ? e.message : (e as Error).message;
          results.push({ kind: 'error', filename: file.name, message: msg });
        }
      }
      setImporting(false);
      onResults(results);
    },
    [onResults],
  );

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    void handleFiles(e.target.files);
    e.target.value = '';
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(true);
  };

  const onDragLeave = () => setDragging(false);

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files.length > 0) {
      void handleFiles(e.dataTransfer.files);
    }
  };

  return (
    <section
      aria-label="HH 파일 가져오기"
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`rounded-md border-2 border-dashed px-3 py-3 text-xs text-center ${
        dragging ? 'border-accent text-accent bg-accent/10' : 'border-border text-muted'
      }`}
      data-testid="hh-dropzone"
      data-dragging={dragging ? 'true' : undefined}
    >
      <p>PokerStars HH `.txt` 파일을 여기에 끌어다 놓거나</p>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={importing}
        className="mt-1 rounded-md bg-bg-elev border border-border px-2 py-1 text-fg hover:text-accent disabled:opacity-40"
        data-testid="hh-browse"
      >
        {importing ? '가져오는 중…' : '파일 선택'}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept=".txt,text/plain"
        multiple
        onChange={onInputChange}
        className="hidden"
        data-testid="hh-file-input"
      />
    </section>
  );
}
